import { computed, type ComputedRef } from 'vue'
import type { MenuItem } from '~/models/menu-item'
import { MENU_SECTION } from '~/models/menu-item'
import { PermissionEnum } from '~~/models/graphql/generated/types'
import { useUserSessionStore } from '~~/store/user-session'

export function useAdminNavigationMenu(): {
  menu: ComputedRef<MenuItem[]>
} {
  const userSessionStore = useUserSessionStore()

  const menu = computed(() => {
    const menuItems: MenuItem[] = []

    // ----- GRANTING -----

    if (userSessionStore.userHasPermission(PermissionEnum.ViewGrants)) {
      menuItems.push({
        name: 'GPS',
        to: '/grant-processing',
        sectionType: MENU_SECTION.granting,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.ViewCharities)) {
      menuItems.push({
        name: 'Charities',
        to: '/charities',
        sectionType: MENU_SECTION.granting,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.ViewCases)) {
      menuItems.push({
        name: 'Case Records',
        to: '/cases',
        sectionType: MENU_SECTION.granting,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.ViewCaseProcessing)) {
      menuItems.push({
        name: 'Case Reporting',
        to: '/case-reporting',
        sectionType: MENU_SECTION.granting,
      })
    }

    // ----- DAF MANAGEMENT -----

    if (userSessionStore.userHasPermission(PermissionEnum.ViewDafFees)) {
      menuItems.push({
        name: 'DAF Fees Dashboard',
        to: '/daf-fees',
        sectionType: MENU_SECTION.dafManagement,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.EditDafStatements)) {
      menuItems.push({
        name: 'DAF Statements',
        to: '/daf-statements',
        sectionType: MENU_SECTION.dafManagement,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.SageSalesforceSync)) {
      menuItems.push({
        name: 'Sage – Salesforce',
        to: '/sage-salesforce',
        sectionType: MENU_SECTION.dafManagement,
      })
      menuItems.push({
        name: 'Sage Journal Entries',
        to: '/sage-journal-entries',
        sectionType: MENU_SECTION.dafManagement,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.ViewInvestments)) {
      menuItems.push({
        name: 'Investment Dashboard',
        to: '/investments',
        sectionType: MENU_SECTION.dafManagement,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.ViewFunds)) {
      menuItems.push({
        name: 'Funds',
        to: '/funds',
        sectionType: MENU_SECTION.dafManagement,
      })
      menuItems.push({
        name: 'Fund Groups',
        to: '/fund-groups',
        sectionType: MENU_SECTION.dafManagement,
      })
    }

    // ----- APP CONTENT -----

    if (userSessionStore.userHasPermission(PermissionEnum.EditNewsItems)) {
      menuItems.push({
        name: 'Newsfeed',
        to: '/news',
        sectionType: MENU_SECTION.appContent,
      })
    }

    if (
      userSessionStore.userHasPermission(PermissionEnum.EditImpactMeasurements)
    ) {
      menuItems.push({
        name: 'How We Measure Impact',
        to: '/how-we-measure-impact',
        sectionType: MENU_SECTION.appContent,
      })
    }

    if (
      userSessionStore.userHasPermission(PermissionEnum.EditCharityCategories)
    ) {
      menuItems.push({
        name: 'Charity Categories',
        to: '/charity-categories',
        sectionType: MENU_SECTION.appContent,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.ViewMatchCampaigns)) {
      menuItems.push({
        name: 'Match Campaigns',
        to: '/match-campaigns',
        sectionType: MENU_SECTION.appContent,
      })
    }

    if (
      userSessionStore.userHasPermission(
        PermissionEnum.EditMatchCampaignCategories
      )
    ) {
      menuItems.push({
        name: 'Match Campaign Categories',
        to: '/match-campaign-categories',
        sectionType: MENU_SECTION.appContent,
      })
    }

    if (
      userSessionStore.userHasPermission(PermissionEnum.ViewTargetAudiences)
    ) {
      menuItems.push({
        name: 'Target Audiences',
        to: '/target-audiences',
        sectionType: MENU_SECTION.appContent,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.ViewUser)) {
      menuItems.push({
        name: 'Users',
        to: '/users',
        sectionType: MENU_SECTION.appContent,
      })
    }

    // ----- HIFO -----

    if (userSessionStore.userHasPermission(PermissionEnum.ViewHifo)) {
      menuItems.push({
        name: 'HMS',
        to: '/hifo-management',
        sectionType: MENU_SECTION.hifo,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.ViewHifoCascade)) {
      menuItems.push({
        name: 'Cascade',
        to: '/hifo-cascades',
        sectionType: MENU_SECTION.hifo,
      })
    }

    if (
      userSessionStore.userHasPermission(PermissionEnum.ViewRequestsForFunding)
    ) {
      menuItems.push({
        name: 'Requests for funding',
        to: '/requests-for-funding',
        sectionType: MENU_SECTION.hifo,
      })
    }

    if (
      userSessionStore.userHasPermission(
        PermissionEnum.CallHifoRecommendationAgent
      )
    ) {
      menuItems.push({
        name: 'HIFO Finder Preview',
        to: '/hifo-recommendation-preview',
        sectionType: MENU_SECTION.hifo,
      })
    }

    // ----- ADMIN -----

    if (userSessionStore.userHasPermission(PermissionEnum.EditRole)) {
      menuItems.push({
        name: 'Roles',
        to: '/roles',
        sectionType: MENU_SECTION.admin,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.ViewEmployees)) {
      menuItems.push({
        name: 'Employees',
        to: '/employees',
        sectionType: MENU_SECTION.admin,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.DataUploads)) {
      menuItems.push({
        name: 'Data Uploads',
        to: '/data-uploads',
        sectionType: MENU_SECTION.admin,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.ViewEmployees)) {
      menuItems.push({
        name: 'Teams',
        to: '/teams',
        sectionType: MENU_SECTION.admin,
      })
    }

    // ----- GROWTH -----

    if (userSessionStore.userHasPermission(PermissionEnum.ViewPledge)) {
      menuItems.push({
        name: 'Pledges',
        to: '/pledges',
        sectionType: MENU_SECTION.growth,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.ViewAmendedPledge)) {
      menuItems.push({
        name: 'Amended Pledges',
        to: '/amended-pledges',
        sectionType: MENU_SECTION.growth,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.ViewCompanies)) {
      menuItems.push({
        name: 'Companies',
        to: '/companies',
        sectionType: MENU_SECTION.growth,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.ViewContacts)) {
      menuItems.push({
        name: 'Contacts',
        to: '/contacts',
        sectionType: MENU_SECTION.growth,
      })
    }

    // ----- TRAINING -----

    if (userSessionStore.trainingInScope) {
      menuItems.push({
        name: 'My training',
        to: '/training',
        sectionType: MENU_SECTION.training,
      })
    }

    if (
      userSessionStore.userHasPermission(PermissionEnum.ViewTrainingCompliance)
    ) {
      menuItems.push({
        name: 'Compliance',
        to: '/training-admin',
        sectionType: MENU_SECTION.training,
      })
    }

    if (
      userSessionStore.userHasPermission(PermissionEnum.EditTrainingContent)
    ) {
      menuItems.push({
        name: 'Courses',
        to: '/training-admin/courses',
        sectionType: MENU_SECTION.training,
      })
    }

    if (
      userSessionStore.userHasPermission(PermissionEnum.ViewTrainingCompliance)
    ) {
      menuItems.push({
        name: 'Policies',
        to: '/training-admin/policies',
        sectionType: MENU_SECTION.training,
      })
    }

    if (
      userSessionStore.userHasPermission(PermissionEnum.ViewTrainingCompliance)
    ) {
      menuItems.push({
        name: 'Reminders',
        to: '/training-admin/nudges',
        sectionType: MENU_SECTION.training,
      })
    }

    // ----- ASSETS -----

    if (userSessionStore.userHasPermission(PermissionEnum.ViewAssets)) {
      menuItems.push({
        name: 'All assets',
        to: '/assets',
        sectionType: MENU_SECTION.assets,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.ManageAssets)) {
      menuItems.push({
        name: 'Add assets',
        to: '/assets/new-from-order',
        sectionType: MENU_SECTION.assets,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.ViewAssets)) {
      menuItems.push({
        name: 'Device checks',
        to: '/assets/device-checks',
        sectionType: MENU_SECTION.assets,
      })
    }

    if (userSessionStore.userHasPermission(PermissionEnum.ViewAssets)) {
      menuItems.push({
        name: 'Returns',
        to: '/assets/returns',
        sectionType: MENU_SECTION.assets,
      })
    }

    // ----- CONTRACTS -----

    if (userSessionStore.userHasPermission(PermissionEnum.ViewContracts)) {
      menuItems.push({
        name: 'All contracts',
        to: '/contracts',
        sectionType: MENU_SECTION.contracts,
      })
    }

    // ----- EVENTS -----

    if (userSessionStore.userHasPermission(PermissionEnum.ManageEvents)) {
      menuItems.push({
        name: 'Events',
        to: '/events',
        sectionType: MENU_SECTION.events,
      })
    }

    return menuItems
  })

  return { menu }
}
